import {
  getWebsiteProviders,
  updateSearchProvidersWebsiteSearchParams,
} from "@stores/providersWebsiteSearchParams"
import states, { statesByCode } from "@lib/helpers/states.js"
import StatesInput, { Option } from "./StatesInput"
import { useEffect, useRef, useState } from "preact/hooks"
import {
  useProviders,
  WebsiteProvider,
} from "@components/preact/hooks/useProvidersWebsite"
import { Button } from "@components/preact/design-system"
import ProviderCard from "./ProviderCard"
import StatCard from "./StatCard"
import stethoscope from "@assets/home/stethoscope.webp?url"
import { useDeviceType } from "@components/preact/hooks/useDeviceType"
import useHorizontalScrollOnVerticalScroll from "@components/preact/hooks/useHorizontalScrollOnVerticalScroll"

interface NoEnrolledStateProps {
  regionCode: Option
}

const statCards = [
  {
    textBold: "55% of patients",
    text: "book and get care within 24 hours",
    background: "bg-gradient-peach-skyblue-fade",
  },
  {
    textBold: "99% of patients are satisfied",
    text: "with their care and relationship with their provider",
    background: "bg-gradient-skyblue-fade-peach-overlay",
  },
]

const renderRow = (providers: WebsiteProvider[], rowIndex: number) => {
  const ITEMS_PER_ROW = 10
  const STAT_CARD_FREQUENCY = 4

  const providerItems = providers.slice(0, ITEMS_PER_ROW).map((provider) => (
    <div key={provider.id} className="min-w-[280px] shrink-0">
      <ProviderCard providerWebsite={provider} />
    </div>
  ))

  return providerItems.flatMap((item, index) => {
    const elements = [item]

    const shouldInsertStat = (index + 1) % STAT_CARD_FREQUENCY === 0
    if (shouldInsertStat) {
      const stat =
        statCards[Math.floor(index / STAT_CARD_FREQUENCY) % statCards.length]

      elements.push(
        <div
          key={`stat-${rowIndex}-${index}`}
          className="min-w-[280px] shrink-0"
        >
          <StatCard type="24h care" background={stat.background}>
            <span className="text-neutral-60-primary-text text-3xl font-bold leading-9">
              {stat.textBold}
            </span>
            <br />
            <span className="text-neutral-60-primary-text text-3xl font-medium leading-9">
              {stat.text}
            </span>
          </StatCard>
        </div>,
      )
    }

    return elements
  })
}

const renderSkeletonRow = (amount: number) => {
  return Array.from({ length: amount }).map((_, index) => (
    <div key={`skeleton-${index}`} className="min-w-[280px] shrink-0">
      <ProviderCard.Skeleton />
    </div>
  ))
}

export const NoEnrolledState = ({ regionCode }: NoEnrolledStateProps) => {
  return (
    <div className="flex flex-col gap-y-6 items-center justify-center px-4">
      <img src={stethoscope} className="size-28" alt="Stethoscope" />
      <div className="flex flex-col gap-y-2">
        <p className="text-center text-neutral-60-primary-text text-xl font-medium leading-loose">
          We’re not in {regionCode?.value} yet
        </p>
        <p className="text-center text-neutral-50-secondary-text text-base font-normal leading-normal">
          It looks like we don’t have any providers in your state right now.
          <br />
          We’re growing fast, so we hope to be in your area soon!
        </p>
      </div>
    </div>
  )
}

export const FindTheRightProvider = ({
  defaultRegionCode,
}: {
  defaultRegionCode: string
}) => {
  const defaultRegionOption = statesByCode[defaultRegionCode]
  const [regionCode, setRegionCode] = useState<Option | undefined>(
    defaultRegionOption,
  )
  const isMobile = useDeviceType() === "mobile"
  const { websiteProviders, isLoading } = useProviders({
    stateCode: regionCode?.key,
  })

  const firstRowRef = useRef<HTMLDivElement>(null)
  const secondRowRef = useRef<HTMLDivElement>(null)
  const componentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (secondRowRef.current) {
      secondRowRef.current.scrollLeft =
        secondRowRef.current.scrollWidth - secondRowRef.current.clientWidth
    }
  }, [websiteProviders])

  useHorizontalScrollOnVerticalScroll(componentRef, firstRowRef, secondRowRef)

  return (
    <div ref={componentRef} className="flex flex-col gap-y-14">
      <div className="flex flex-col w-full items-center justify-center gap-y-6 px-4">
        <div className="flex flex-col items-center justify-center gap-y-2">
          <p className="font-semibold text-center text-3xl lg:text-4xl">
            Find the right provider in
          </p>
          <StatesInput
            label="State Input with Dropdown"
            options={states}
            defaultValue={regionCode}
            onChange={(value) => {
              updateSearchProvidersWebsiteSearchParams({ stateCode: value.key })
              getWebsiteProviders()
              setRegionCode(value as Option)
            }}
          />
        </div>
        <p className="text-center justify-center text-neutral-50-secondary-text text-lg font-normal leading-relaxed lg:w-[748px]">
          We connect you to a carefully curated doctors vetted for clinical
          excellence and superior patient experience. With a wide variety of
          specialities, backgrounds, and personalities, you'll always find
          someone in tune with your individual needs.
        </p>
        <Button
          size="large"
          href="https://booking-v2.circlemedical.com/providers/"
        >
          Find my provider
        </Button>
      </div>
      {regionCode?.enrolled ? (
        <div className="bg-white space-y-6 w-screen px-6 md:px-0 items-center justify-center">
          {isMobile ? (
            <div
              ref={firstRowRef}
              className="flex gap-6 overflow-x-auto hide-scrollbar items-center justify-center"
            >
              {isLoading
                ? renderSkeletonRow(20)
                : renderRow(websiteProviders.slice(0, 20), 1)}
            </div>
          ) : (
            <>
              <div
                ref={firstRowRef}
                className="flex gap-6 overflow-x-auto hide-scrollbar items-center justify-center"
              >
                {isLoading
                  ? renderSkeletonRow(10)
                  : renderRow(websiteProviders.slice(0, 10), 1)}
              </div>
              <div
                ref={secondRowRef}
                className="flex gap-6 overflow-x-auto hide-scrollbar items-center justify-center"
              >
                {isLoading
                  ? renderSkeletonRow(10)
                  : renderRow(websiteProviders.slice(10, 20), 2)}
              </div>
            </>
          )}
        </div>
      ) : (
        <NoEnrolledState regionCode={regionCode} />
      )}
    </div>
  )
}
